import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Output } from '@angular/core';
import { SupportService } from '../support.service';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-support-ticket-form',
    templateUrl: './support-ticket-form.component.html',
    styleUrls: ['./support-ticket-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SupportTicketFormComponent {
    @Output() ticketCreated = new EventEmitter<void>();
    @Output() cancel = new EventEmitter<void>();

    summary = '';
    description = '';
    submitting = false;
    capturing = false;
    error: string | null = null;
    attachments: File[] = [];

    constructor(
        private supportService: SupportService,
        private cdr: ChangeDetectorRef,
    ) {}

    get isValid(): boolean {
        return this.summary.trim().length > 0 && this.description.trim().length > 0;
    }

    // Handle paste (Ctrl+V) for screenshots
    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
        const items = event.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                const file = items[i].getAsFile();
                if (file) {
                    const named = new File([file], `screenshot-${Date.now()}.png`, { type: file.type });
                    this.attachments.push(named);
                    this.cdr.markForCheck();
                }
                event.preventDefault();
                break;
            }
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files) {
            for (let i = 0; i < input.files.length; i++) {
                this.attachments.push(input.files[i]);
            }
            input.value = '';
            this.cdr.markForCheck();
        }
    }

    removeAttachment(index: number): void {
        this.attachments.splice(index, 1);
        this.cdr.markForCheck();
    }

    async captureScreenshot(): Promise<void> {
        if (this.capturing) return;
        this.capturing = true;
        this.cdr.markForCheck();

        let track: MediaStreamTrack | null = null;
        try {
            const stream = await (navigator.mediaDevices as any).getDisplayMedia({
                video: { displaySurface: 'browser' },
                preferCurrentTab: true,
            });

            track = stream.getVideoTracks()[0];
            const imageCapture = new (window as any).ImageCapture(track);
            const bitmap = await imageCapture.grabFrame();

            const canvas = document.createElement('canvas');
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            canvas.getContext('2d')!.drawImage(bitmap, 0, 0);

            const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
            if (blob) {
                const file = new File([blob], `page-screenshot-${Date.now()}.png`, { type: 'image/png' });
                this.attachments.push(file);
            }
        } catch {
            // User cancelled or browser doesn't support it — silently ignore
        } finally {
            track?.stop();
            this.capturing = false;
            this.cdr.markForCheck();
        }
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    }

    submit(): void {
        if (!this.isValid || this.submitting) return;

        this.submitting = true;
        this.error = null;

        this.supportService
            .createTicket({
                summary: this.summary.trim(),
                description: this.description.trim(),
            })
            .subscribe({
                next: result => {
                    const issueKey = result.issueKey;

                    // Upload attachments if any
                    if (this.attachments.length > 0 && issueKey) {
                        const uploads = this.attachments.map(f => this.supportService.uploadAttachment(issueKey, f));
                        forkJoin(uploads).subscribe({
                            next: () => {
                                this.submitting = false;
                                this.ticketCreated.emit();
                            },
                            error: () => {
                                // Ticket created but attachment failed — still navigate to list
                                this.submitting = false;
                                this.ticketCreated.emit();
                            },
                        });
                    } else {
                        this.submitting = false;
                        this.ticketCreated.emit();
                    }
                },
                error: () => {
                    this.error = 'Failed to create request. Please try again.';
                    this.submitting = false;
                    this.cdr.markForCheck();
                },
            });
    }
}
